import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  CheckCircle,
  Clock,
  AlertTriangle,
  Loader,
  Trash2,
  ChevronDown,
  ChevronRight,
  UserCircle,
  Shield,
  Target,
  Zap,
  BarChart3,
  MessageSquare,
  Hash,
  BookOpen,
  X,
  Sparkles,
  Users,
  LayoutGrid,
  ClipboardList,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type {
  Task,
  WorkspaceItem,
  WorkspaceMember,
  WorkspaceItemStatus,
  WorkspaceRole,
  Priority,
} from '../types';
import {
  getWorkspaceMembers,
  subscribeWorkspaceItems,
  createWorkspaceItem,
  updateWorkspaceItemStatus,
  deleteWorkspaceItem,
  updateMemberRole,
} from '../api/workspace';
import { getDisplayNameForUser } from '../api/connections';
import TaskChat from './TaskChat';
import PromptBoard from './PromptBoard';

export interface WorkspaceChatPeer {
  connectionId: string;
  otherUserId: string;
  otherName: string;
}

interface Props {
  task: Task;
  /** Accepted connections for DMs (from task page) */
  chatPeers?: WorkspaceChatPeer[];
}

const GUIDE_KEY = 'gnk_workspace_guide_dismissed';

const STATUS_CONFIG: Record<WorkspaceItemStatus, { label: string; color: string; bg: string; icon: typeof Clock }> = {
  pending:       { label: 'Pending',     color: 'text-yellow-400',  bg: 'bg-yellow-400/15 border-yellow-400/30', icon: Clock },
  'in-progress': { label: 'In Progress', color: 'text-tech-cyan',   bg: 'bg-tech-cyan/15 border-tech-cyan/30',   icon: Zap },
  completed:     { label: 'Completed',   color: 'text-tech-accent', bg: 'bg-tech-accent/15 border-tech-accent/30', icon: CheckCircle },
  blocked:       { label: 'Blocked',     color: 'text-amber-400',   bg: 'bg-amber-400/15 border-amber-400/30',   icon: AlertTriangle },
};

const PRIORITY_COLORS: Record<Priority, string> = {
  low: 'text-tech-text-muted',
  medium: 'text-tech-cyan',
  high: 'text-amber-400',
  critical: 'text-red-400',
};

const ROLE_LABELS: Record<WorkspaceRole, { label: string; color: string }> = {
  owner:       { label: 'Owner',       color: 'text-tech-cyan bg-tech-cyan/10 border-tech-cyan/30' },
  collaborator:{ label: 'Collaborator',color: 'text-tech-accent bg-tech-accent/10 border-tech-accent/30' },
  reviewer:    { label: 'Reviewer',    color: 'text-purple-400 bg-purple-400/10 border-purple-400/30' },
  contributor: { label: 'Contributor', color: 'text-blue-400 bg-blue-400/10 border-blue-400/30' },
};

const TaskWorkspace: React.FC<Props> = ({ task, chatPeers = [] }) => {
  const { currentUser } = useAuth();
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [items, setItems] = useState<WorkspaceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formAssignee, setFormAssignee] = useState('');
  const [formPriority, setFormPriority] = useState<Priority>('medium');
  const [creating, setCreating] = useState(false);

  const [editingRole, setEditingRole] = useState<string | null>(null);

  // DM slide-over drawer
  const [dmDrawer, setDmDrawer] = useState<WorkspaceChatPeer | null>(null);

  const [guideOpen, setGuideOpen] = useState(false);
  const [guideHintVisible, setGuideHintVisible] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<'duties' | 'team' | 'board' | 'chat'>('duties');

  const isOwner = currentUser?.uid === task.userId;

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(GUIDE_KEY);
      setGuideHintVisible(!dismissed);
    } catch {
      setGuideHintVisible(true);
    }
  }, []);

  useEffect(() => {
    let unsubItems: (() => void) | undefined;

    const load = async () => {
      setLoading(true);
      try {
        const rawMembers = await getWorkspaceMembers(task.id);

        const ownerInList = rawMembers.some((m) => m.userId === task.userId);
        if (!ownerInList) {
          rawMembers.unshift({
            userId: task.userId,
            displayName: task.userName || 'Owner',
            role: 'owner',
            joinedAt: task.createdAt,
          });
        } else {
          const ownerMember = rawMembers.find((m) => m.userId === task.userId);
          if (ownerMember && ownerMember.role !== 'owner') ownerMember.role = 'owner';
        }

        await Promise.all(
          rawMembers.map(async (m) => {
            if (m.displayName && m.displayName !== 'Collaborator') return;
            try {
              const name = await getDisplayNameForUser(m.userId, 'Collaborator');
              m.displayName = name;
            } catch { /* keep default */ }
          })
        );

        setMembers(rawMembers);
        setLoading(false);

        unsubItems = subscribeWorkspaceItems(task.id, (nextItems) => {
          setItems(nextItems);
        });
      } catch (err) {
        console.error('Error loading workspace:', err);
        setMembers([
          {
            userId: task.userId,
            displayName: task.userName || 'Owner',
            role: 'owner',
            joinedAt: task.createdAt,
          },
        ]);
        setLoading(false);
        unsubItems = subscribeWorkspaceItems(task.id, setItems);
      }
    };

    load();
    return () => {
      if (unsubItems) unsubItems();
    };
  }, [task.id, task.userId, task.userName, task.createdAt]);

  const itemsByUser = useMemo(() => {
    const map = new Map<string, WorkspaceItem[]>();
    members.forEach((m) => map.set(m.userId, []));
    items.forEach((item) => {
      const list = map.get(item.assignedTo) || [];
      list.push(item);
      map.set(item.assignedTo, list);
    });
    return map;
  }, [members, items]);

  const stats = useMemo(() => {
    const total = items.length;
    const completed = items.filter((i) => i.status === 'completed').length;
    const inProgress = items.filter((i) => i.status === 'in-progress').length;
    const pending = items.filter((i) => i.status === 'pending').length;
    const blocked = items.filter((i) => i.status === 'blocked').length;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, inProgress, pending, blocked, progress };
  }, [items]);

  const dismissGuideHint = () => {
    try {
      localStorage.setItem(GUIDE_KEY, '1');
    } catch { /* ignore */ }
    setGuideHintVisible(false);
    setGuideOpen(false);
  };

  const handleCreateItem = async () => {
    if (!formTitle.trim() || !formAssignee || !currentUser) return;
    setCreating(true);
    try {
      const assigneeMember = members.find((m) => m.userId === formAssignee);
      await createWorkspaceItem(task.id, {
        title: formTitle.trim(),
        description: formDesc.trim(),
        assignedTo: formAssignee,
        assignedToName: assigneeMember?.displayName || 'Member',
        assignedBy: currentUser.uid,
        assignedByName: currentUser.displayName || 'You',
        priority: formPriority,
      });
      setFormTitle('');
      setFormDesc('');
      setFormAssignee('');
      setFormPriority('medium');
      setShowForm(false);
    } catch (err) {
      console.error('Error creating item:', err);
    } finally {
      setCreating(false);
    }
  };

  const cycleStatus = async (item: WorkspaceItem) => {
    const order: WorkspaceItemStatus[] = ['pending', 'in-progress', 'completed', 'blocked'];
    const idx = order.indexOf(item.status);
    const next = order[(idx + 1) % order.length];
    await updateWorkspaceItemStatus(item.id, next);
  };

  const handleRoleChange = async (memberId: string, newRole: WorkspaceRole) => {
    try {
      await updateMemberRole(task.id, memberId, newRole);
      setMembers((prev) =>
        prev.map((m) => (m.userId === memberId ? { ...m, role: newRole } : m))
      );
      setEditingRole(null);
    } catch (err) {
      console.error('Error updating role:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="w-8 h-8 text-tech-cyan animate-spin" />
        <span className="ml-3 text-tech-text-muted">Preparing workspace…</span>
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl border border-tech-border/60 bg-[#080c14]/80 overflow-hidden">
      {/* Guide launcher */}
      {guideHintVisible && (
        <button
          type="button"
          onClick={() => setGuideOpen(true)}
          className="absolute bottom-4 left-4 z-20 flex items-center gap-2 px-3 py-2 rounded-xl bg-white text-slate-900 text-sm font-semibold shadow-lg shadow-black/40 border border-white/20 hover:bg-slate-100 transition-colors"
        >
          <BookOpen className="w-4 h-4" />
          Guide
        </button>
      )}

      {guideOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6 text-slate-800 relative">
            <button
              type="button"
              onClick={() => setGuideOpen(false)}
              className="absolute top-4 right-4 p-1 rounded-lg hover:bg-slate-100 text-slate-500"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-slate-900 pr-8">Welcome to the task workspace</h2>
            <p className="text-sm text-slate-600 mt-1 mb-5">Here's how to get the most out of it.</p>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <span className="text-2xl shrink-0" aria-hidden>💬</span>
                <div>
                  <strong className="text-slate-900">Left — Channels</strong>
                  <p className="text-slate-600 mt-0.5">Use <strong># team</strong> for the Prompt Board, or pick a teammate for a private DM thread.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-2xl shrink-0" aria-hidden>📋</span>
                <div>
                  <strong className="text-slate-900">Center — Duties</strong>
                  <p className="text-slate-600 mt-0.5">Roles, assignments, and progress for everyone on this task.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-2xl shrink-0" aria-hidden>⚡</span>
                <div>
                  <strong className="text-slate-900">Right — Prompt Board</strong>
                  <p className="text-slate-600 mt-0.5">Structured updates with tagged links, keywords, env vars, status, and tools.</p>
                </div>
              </li>
            </ul>
            <button
              type="button"
              onClick={dismissGuideHint}
              className="mt-6 text-sm text-blue-600 hover:underline"
            >
              Don&apos;t show the Guide button again
            </button>
          </div>
        </div>
      )}

      {/* ── DM slide-over drawer ── */}
      {dmDrawer && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm"
            onClick={() => setDmDrawer(null)}
          />
          {/* Drawer panel */}
          <div className="fixed right-0 top-0 bottom-0 z-[95] w-full max-w-sm bg-[#080c14] border-l border-tech-border/60 flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-tech-border/40 shrink-0">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-tech-accent" />
                <span className="font-semibold text-tech-text text-sm">DM · {dmDrawer.otherName}</span>
              </div>
              <button
                type="button"
                onClick={() => setDmDrawer(null)}
                className="p-1.5 rounded-lg text-tech-text-muted hover:text-tech-text hover:bg-white/5 transition-colors"
                aria-label="Close DM"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              <TaskChat
                connectionId={dmDrawer.connectionId}
                taskId={task.id}
                otherParticipantName={dmDrawer.otherName}
                compact
              />
            </div>
          </div>
        </>
      )}

      {/* Mobile tab bar — visible below lg */}
      <div className="flex lg:hidden border-b border-tech-border/40 bg-tech-dark/50">
        {([
          { id: 'duties' as const, label: 'Duties',  icon: Target },
          { id: 'team'   as const, label: 'Team',    icon: Hash },
          { id: 'board'  as const, label: 'Board',   icon: ClipboardList },
          { id: 'chat'   as const, label: 'Chat',    icon: MessageSquare },
        ]).map((t) => {
          const Icon = t.icon;
          const active = mobilePanel === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setMobilePanel(t.id)}
              className={`flex-1 flex items-center justify-center gap-1 py-3 text-[11px] font-semibold transition-colors ${
                active
                  ? 'text-tech-cyan border-b-2 border-tech-cyan bg-tech-cyan/5'
                  : 'text-tech-text-muted hover:text-tech-text'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,180px)_minmax(0,1fr)_minmax(280px,380px)] min-h-[min(75vh,780px)]">

        {/* ── Left: channels (desktop only) ── */}
        <aside className="hidden lg:flex lg:flex-col lg:border-r border-tech-border/40 bg-tech-dark/40 overflow-y-auto shrink-0">
          <div className="flex items-center gap-2 px-3 py-3 border-b border-tech-border/30">
            <LayoutGrid className="w-4 h-4 text-tech-cyan" />
            <span className="text-xs font-bold uppercase tracking-wider text-tech-text-muted">Workspace</span>
          </div>
          <nav className="flex flex-col p-2 gap-1 min-w-0">
            {/* # team → just a label since Prompt Board is always in right panel */}
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30">
              <Hash className="w-4 h-4 shrink-0" />
              <span className="font-medium truncate">team</span>
            </div>

            <div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-tech-text-muted/80">
              Direct
            </div>
            {chatPeers.map((p) => (
              <button
                key={p.connectionId}
                type="button"
                onClick={() => setDmDrawer(p)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-left text-sm whitespace-nowrap transition-colors ${
                  dmDrawer?.connectionId === p.connectionId
                    ? 'bg-tech-accent/15 text-tech-accent border border-tech-accent/30'
                    : 'text-tech-text-muted hover:bg-white/5 hover:text-tech-text border border-transparent'
                }`}
              >
                <MessageSquare className="w-4 h-4 shrink-0 opacity-70" />
                <span className="truncate">{p.otherName}</span>
              </button>
            ))}
            {chatPeers.length === 0 && (
              <p className="px-3 py-2 text-[11px] text-tech-text-muted max-w-[180px]">
                Connect with collaborators to unlock DMs.
              </p>
            )}
          </nav>
        </aside>

        {/* ── Center: duties & roles ── */}
        <main className={`overflow-y-auto custom-scrollbar lg:border-r border-tech-border/40 p-4 space-y-4 min-h-[280px] ${mobilePanel !== 'duties' ? 'hidden lg:block' : ''}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-tech-cyan/10 border border-tech-cyan/20">
                <Target className="w-5 h-5 text-tech-cyan" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-tech-text">Roles & duties</h2>
                <p className="text-xs text-tech-text-muted flex items-center gap-1">
                  <Users className="w-3 h-3" /> {members.length} member{members.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-tech-dark transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
            >
              <Plus className="w-4 h-4" /> Assign duty
            </button>
          </div>

          {stats.total > 0 && (
            <div className="rounded-xl border border-tech-cyan/20 bg-tech-cyan/5 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-tech-text-muted flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" /> Overall progress
                </span>
                <span className="text-xs font-bold text-tech-cyan">{stats.progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-tech-gray overflow-hidden mb-3">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${stats.progress}%`,
                    background:
                      stats.progress === 100
                        ? 'linear-gradient(90deg, #10b981, #34d399)'
                        : 'linear-gradient(90deg, #0066ff, #00d9ff)',
                  }}
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {([
                  { label: 'Pending', count: stats.pending, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
                  { label: 'Active', count: stats.inProgress, color: 'text-tech-cyan', bg: 'bg-tech-cyan/10' },
                  { label: 'Done', count: stats.completed, color: 'text-tech-accent', bg: 'bg-tech-accent/10' },
                  { label: 'Blocked', count: stats.blocked, color: 'text-amber-400', bg: 'bg-amber-400/10' },
                ] as const).map((s) => (
                  <div key={s.label} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${s.bg}`}>
                    <span className={`text-sm font-bold ${s.color}`}>{s.count}</span>
                    <span className="text-[10px] text-tech-text-muted">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showForm && (
            <div className="rounded-xl border border-tech-cyan/25 bg-tech-dark/30 p-4 space-y-3 animate-fade-in">
              <h3 className="text-sm font-bold text-tech-text flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-tech-cyan" /> New duty
              </h3>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="What needs to be done?"
                className="w-full px-3 py-2 rounded-lg bg-tech-dark border border-tech-border text-tech-text text-sm"
              />
              <textarea
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                placeholder="Details (optional)"
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-tech-dark border border-tech-border text-tech-text text-sm resize-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={formAssignee}
                  onChange={(e) => setFormAssignee(e.target.value)}
                  className="rounded-lg bg-tech-dark border border-tech-border px-3 py-2 text-tech-text text-sm"
                >
                  <option value="">Assign to…</option>
                  {members.map((m) => (
                    <option key={m.userId} value={m.userId}>{m.displayName}</option>
                  ))}
                </select>
                <select
                  value={formPriority}
                  onChange={(e) => setFormPriority(e.target.value as Priority)}
                  className="rounded-lg bg-tech-dark border border-tech-border px-3 py-2 text-tech-text text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm border border-tech-border rounded-lg text-tech-text-muted">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateItem}
                  disabled={creating || !formTitle.trim() || !formAssignee}
                  className="px-4 py-1.5 text-sm font-semibold text-tech-dark rounded-lg disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, #00ff88, #00cc6a)' }}
                >
                  {creating ? <Loader className="w-4 h-4 animate-spin" /> : 'Add'}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {members.map((member) => {
              const userItems = itemsByUser.get(member.userId) || [];
              const isExpanded = expandedUser === member.userId;
              const completedCount = userItems.filter((i) => i.status === 'completed').length;
              const isMe = member.userId === currentUser?.uid;
              const roleConfig = ROLE_LABELS[member.role];

              return (
                <div key={member.userId} className="rounded-xl border border-tech-border/40 overflow-hidden bg-tech-gray/20">
                  <button
                    type="button"
                    onClick={() => setExpandedUser(isExpanded ? null : member.userId)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-tech-cyan to-blue-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
                      {member.displayName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-tech-text text-sm truncate">
                          {member.displayName}{isMe ? ' (You)' : ''}
                        </span>
                        {editingRole === member.userId && isOwner && member.role !== 'owner' ? (
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.userId, e.target.value as WorkspaceRole)}
                            onBlur={() => setEditingRole(null)}
                            onClick={(e) => e.stopPropagation()}
                            className="text-[10px] rounded bg-tech-dark border border-tech-border px-1 py-0.5"
                            autoFocus
                          >
                            <option value="collaborator">Collaborator</option>
                            <option value="reviewer">Reviewer</option>
                            <option value="contributor">Contributor</option>
                          </select>
                        ) : (
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-md border font-semibold ${roleConfig.color} ${isOwner && member.role !== 'owner' ? 'cursor-pointer' : ''}`}
                            onClick={(e) => {
                              if (isOwner && member.role !== 'owner') {
                                e.stopPropagation();
                                setEditingRole(member.userId);
                              }
                            }}
                          >
                            <Shield className="w-2.5 h-2.5 inline mr-0.5" />
                            {roleConfig.label}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-tech-text-muted mt-0.5">
                        {userItems.length} dut{userItems.length !== 1 ? 'ies' : 'y'}
                        {userItems.length > 0 && ` · ${completedCount}/${userItems.length} done`}
                      </div>
                    </div>
                    {isExpanded ? <ChevronDown className="w-4 h-4 shrink-0 text-tech-text-muted" /> : <ChevronRight className="w-4 h-4 shrink-0 text-tech-text-muted" />}
                  </button>
                  {isExpanded && (
                    <div className="border-t border-tech-border/30 px-3 pb-3 space-y-2">
                      {userItems.length === 0 ? (
                        <p className="text-xs text-tech-text-muted text-center py-3">No duties yet</p>
                      ) : (
                        userItems.map((item) => {
                          const sc = STATUS_CONFIG[item.status];
                          const StatusIcon = sc.icon;
                          const canModify = isMe || isOwner;
                          return (
                            <div key={item.id} className={`flex items-start gap-2 p-2 rounded-lg border ${sc.bg}`}>
                              {canModify ? (
                                <button type="button" onClick={() => cycleStatus(item)} className={`shrink-0 ${sc.color}`}>
                                  <StatusIcon className="w-4 h-4" />
                                </button>
                              ) : (
                                <StatusIcon className={`w-4 h-4 shrink-0 ${sc.color}`} />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${item.status === 'completed' ? 'line-through text-tech-text-muted' : 'text-tech-text'}`}>{item.title}</p>
                                {item.description && <p className="text-[11px] text-tech-text-muted line-clamp-2">{item.description}</p>}
                                <p className="text-[10px] text-tech-text-muted mt-0.5">
                                  <span className={PRIORITY_COLORS[item.priority]}>{item.priority}</span> · by {item.assignedByName}
                                </p>
                              </div>
                              {(isOwner || item.assignedBy === currentUser?.uid) && (
                                <button
                                  type="button"
                                  onClick={() => window.confirm('Delete this duty?') && deleteWorkspaceItem(item.id)}
                                  className="text-tech-text-muted/50 hover:text-amber-400"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {members.length <= 1 && items.length === 0 && (
            <div className="text-center py-6 rounded-xl border border-dashed border-tech-border/50">
              <UserCircle className="w-10 h-10 text-tech-text-muted/30 mx-auto mb-2" />
              <p className="text-xs text-tech-text-muted">Invite collaborators — they&apos;ll show up here with roles and duties.</p>
            </div>
          )}
        </main>

        {/* ── Right: Prompt Board ── */}
        <section className={`flex flex-col min-h-[320px] lg:min-h-0 bg-[#0a0e16] ${mobilePanel !== 'board' ? 'hidden lg:flex' : ''}`}>
          {currentUser ? (
            <PromptBoard
              taskId={task.id}
              currentUserId={currentUser.uid}
              currentUserName={currentUser.displayName || 'You'}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-tech-text-muted text-sm">
              Sign in to use the Prompt Board.
            </div>
          )}
        </section>

        {/* ── Mobile: Team panel (channel list + DM peers) ── */}
        {mobilePanel === 'team' && (
          <div className="lg:hidden flex flex-col p-4 gap-3 min-h-[280px]">
            <p className="text-xs font-bold uppercase tracking-wider text-tech-text-muted">Channels</p>
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm bg-tech-cyan/15 text-tech-cyan border border-tech-cyan/30">
              <Hash className="w-4 h-4 shrink-0" />
              <span className="font-medium">team (Prompt Board)</span>
            </div>
            {chatPeers.length > 0 && (
              <>
                <p className="text-[10px] font-bold uppercase tracking-wider text-tech-text-muted mt-2">Direct Messages</p>
                {chatPeers.map((p) => (
                  <button
                    key={p.connectionId}
                    type="button"
                    onClick={() => setDmDrawer(p)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-tech-text-muted hover:bg-white/5 hover:text-tech-text border border-transparent transition-colors"
                  >
                    <MessageSquare className="w-4 h-4 shrink-0 opacity-70" />
                    <span>{p.otherName}</span>
                  </button>
                ))}
              </>
            )}
            {chatPeers.length === 0 && (
              <p className="text-xs text-tech-text-muted">Connect with collaborators to unlock DMs.</p>
            )}
          </div>
        )}

        {/* ── Mobile: Chat panel (DM list) ── */}
        {mobilePanel === 'chat' && (
          <div className="lg:hidden flex flex-col p-4 gap-3 min-h-[280px]">
            <p className="text-xs font-bold uppercase tracking-wider text-tech-text-muted">Direct Messages</p>
            {chatPeers.length === 0 ? (
              <p className="text-sm text-tech-text-muted">No direct messages yet. Connect with collaborators on this task.</p>
            ) : (
              chatPeers.map((p) => (
                <button
                  key={p.connectionId}
                  type="button"
                  onClick={() => setDmDrawer(p)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl border border-tech-border/40 text-left hover:bg-white/5 transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-tech-accent to-blue-500 flex items-center justify-center text-white font-bold text-xs shrink-0">
                    {p.otherName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-tech-text">{p.otherName}</p>
                    <p className="text-xs text-tech-text-muted">Tap to open DM</p>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskWorkspace;
